class CustomVideoPlayer {
  constructor(videoContainer) {
    this.videoContainer = videoContainer;
    this.player = null;
    this.button = videoContainer.querySelector('.video-control');
    this.muteButton = videoContainer.querySelector('.video-control--mute');
    this.fullscreenButton = videoContainer.querySelector('.video-control--fullscreen');
    
    if (
      this.fullscreenButton &&
      /iPhone/.test(navigator.userAgent) &&
      window.innerWidth <= 767
    ) {
      this.fullscreenButton.style.display = 'none';
    }
    this.timeline = videoContainer.querySelector('.timeline');
    this.timelineProgress = videoContainer.querySelector('.timeline-progress');
    this.isAutoplay = this.button?.dataset.autoplay === 'true' || this.button?.dataset.autoplay === '1';
    this.isPlaying = false; 
    this.isMuted = true;
    this.isFullscreen = false;
    this.lastMouseMove = Date.now();
    this.inactivityTimeout = null;
    this.durationElement = videoContainer.querySelector('.video-duration');
    this.hasPlayed = false;
    this.userPaused = false; 
    this.isMobile = window.innerWidth <= 1023 || /Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
    this.threshold = this.isMobile ? 0.4 : 0; 

    if (this.button) {
      this.init();
    }
  }

  init() {
    const vimeoWrapper = this.videoContainer.querySelector('.vimeo-wrapper');
    const nativeVideo = this.videoContainer.querySelector('.shopify-video');

    this.vimeoWrapper = vimeoWrapper;
    this.videoSrcDesktop = this.vimeoWrapper.dataset.srcDesktop;
    this.videoSrcMobile = this.vimeoWrapper.dataset.srcMobile;
    this.desktopVideoId = this.videoSrcDesktop.split('https://vimeo.com/')[0].split('?')[0];
    this.mobileVideoId = this.videoSrcMobile.split('https://vimeo.com/')[0].split('?')[0];
    this.aspectRatio = this.vimeoWrapper.dataset.aspectRatio;
    this.isMobile = window.innerWidth < 768;

    if (vimeoWrapper) {

      const options = {
        id: this.isMobile ? this.mobileVideoId : this.desktopVideoId,
        quality: "auto",
        autoplay: this.isMobile ? false : true,
        loop: true,
        muted: true,
        controls: false,
        background: true,
        playsinline: true,
        initial_quality: this.isMobile ? "360p" : "auto",
      };


      this.player = new Vimeo.Player(vimeoWrapper, options);
      this.isVimeo = true;

      this.player.ready().then(() => {

        this.setupEventListeners();
        if (this.timeline) {
          this.setupTimelineUpdates();
        }

        if (this.durationElement) {
          this.player.getDuration().then(duration => {
            const minutes = Math.floor(duration / 60);
            const seconds = Math.floor(duration % 60);
            this.durationElement.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
          });
        }
      });
    } else if (nativeVideo) {
      this.player = nativeVideo;
      this.isVimeo = false;

      this.player.pause();
      this.isPlaying = false;
      this.button.classList.remove('is-playing');

      this.setupEventListeners();
      if (this.timeline) {
        this.setupTimelineUpdates();
      }

      if (this.durationElement) {
        this.player.addEventListener('loadedmetadata', () => {
          const duration = this.player.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          this.durationElement.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        });
      }
    }

    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isPlaying && !this.userPaused && (this.isAutoplay || this.hasPlayed)) {
          this.hasPlayed = true;
          this.playVideo();
        }

        if (!entry.isIntersecting) {
          this.pauseVideo();
        }
      });
    }, {
      threshold: this.threshold,
      rootMargin: '0px'
    });

    observer.observe(this.videoContainer);
  }

  playVideo() {
    if (this.isVimeo) {
      this.player.play().then(() => {
        this.isPlaying = true;
        this.button.classList.add('is-playing', 'has-played');
      }).catch(console.error);
    } else {
      this.player.muted = true; 
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /WebKit/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);

      const tryPlay = (attempt = 0) => {
        this.player.play().then(() => {
          this.isPlaying = true;
          this.button.classList.add('is-playing', 'has-played');
          
          const fallback = this.videoContainer.querySelector('.video-autoplay-fallback');
          if (fallback) fallback.remove();
        }).catch(err => {
          if (attempt < 3) {
            setTimeout(() => tryPlay(attempt + 1), 300 * (attempt + 1));
          } else {
            this.isPlaying = false;
            this.button.classList.remove('is-playing');
            
            if (isIOSSafari && !this.videoContainer.querySelector('.video-autoplay-fallback')) {
              const fallback = document.createElement('div');
              fallback.className = 'video-autoplay-fallback';
              fallback.style = 'position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.3);color:#fff;display:flex;align-items:center;justify-content:center;z-index:10;font-size:1.1em;text-align:center;pointer-events:auto;';
              var tapToPlayMsg = window.theme?.strings?.video?.tapToPlay || 'Tap to play video';
              fallback.innerHTML = window.DOMPurify ? window.DOMPurify.sanitize(tapToPlayMsg) : tapToPlayMsg;
              fallback.addEventListener('click', () => {
                this.player.play();
                fallback.remove();
              });
              
              fallback.addEventListener('touchstart', () => {
                this.player.play();
                fallback.remove();
              });
              this.videoContainer.style.position = 'relative';
              this.videoContainer.appendChild(fallback);
              
              if (!this._autoplayAlertShown) {
                var autoplayFailedMsg = window.theme?.strings?.video?.autoplayFailed || 'Autoplay failed. Tap the video to play.';
                alert(autoplayFailedMsg);
                this._autoplayAlertShown = true;
              }
            }
          }
        });
      };

      if (this.player.readyState >= 3) { 
        tryPlay();
      } else {
        const onCanPlay = () => {
          tryPlay();
          this.player.removeEventListener('canplay', onCanPlay);
        };
        this.player.addEventListener('canplay', onCanPlay);
      }
    }
  }

  pauseVideo() {
    if (this.isVimeo) {
      this.player.pause().then(() => {
        this.isPlaying = false;
        this.button.classList.remove('is-playing');
      }).catch(console.error);
    } else {
      this.player.pause();
      this.isPlaying = false;
      this.button.classList.remove('is-playing');
    }
  }

  setupEventListeners() {
    this.button.addEventListener('click', () => this.togglePlay());

    if (this.muteButton) {
      this.muteButton.addEventListener('click', () => {
        this.isMuted = !this.isMuted;
        if (this.isVimeo) {
          this.player.setMuted(this.isMuted);
        } else {
          this.player.muted = this.isMuted;
        }
        this.muteButton.classList.toggle('is-unmuted');
      });
    }

    if (this.fullscreenButton) {
      this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
    }

    this.videoContainer.addEventListener('click', (e) => {
      if (e.target.closest('.video-control') || e.target.closest('.timeline')) return;
      this.togglePlay();
    });

    if (this.timeline) {
      this.timeline.addEventListener('click', (e) => {
        const rect = this.timeline.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;

        if (this.isVimeo) {
          this.player.getDuration().then(duration => {
            this.player.setCurrentTime(duration * percent);
          });
        } else {
          this.player.currentTime = this.player.duration * percent;
        }
      });
    }

    if (this.isVimeo) {
      this.player.on('play', () => {
        this.isPlaying = true;
        this.button.classList.add('is-playing', 'has-played');
      });

      this.player.on('pause', () => {
        this.isPlaying = false;
        this.button.classList.remove('is-playing');
      });
    } else {
      this.player.addEventListener('play', () => {
        this.isPlaying = true;
        this.button.classList.add('is-playing', 'has-played');
      });

      this.player.addEventListener('pause', () => {
        this.isPlaying = false;
        this.button.classList.remove('is-playing');
      });
    }

    if (this.fullscreenButton) {
      document.addEventListener('fullscreenchange', () => {
        this.isFullscreen = document.fullscreenElement === this.videoContainer;
        this.fullscreenButton.classList.toggle('is-fullscreen', this.isFullscreen);
        if (!this.isFullscreen) {
          const controlsTop = this.videoContainer.querySelector('.video-controls--top');
          const timelineContainer = this.videoContainer.querySelector('.timeline-container');
          if (controlsTop) controlsTop.style.display = 'flex';
          if (timelineContainer) timelineContainer.style.display = 'flex';
        }
      });
    }
  }

  setupTimelineUpdates() {
    this.timelineInterval = setInterval(() => {
      if (this.isVimeo) {
        this.player.getCurrentTime().then(currentTime => {
          this.player.getDuration().then(duration => {
            const progress = (currentTime / duration) * 100;
            this.timelineProgress.style.width = `${progress}%`;
          });
        });
      } else {
        const currentTime = this.player.currentTime;
        const duration = this.player.duration;
        if (duration) {
          const progress = (currentTime / duration) * 100;
          this.timelineProgress.style.width = `${progress}%`;
        }
      }
    }, 100);
  }

  togglePlay() {
    if (this.isVimeo) {
      if (this.isPlaying) {
        this.userPaused = true;
        this.player.pause().then(() => {
          this.button.classList.remove('is-playing');
          this.isPlaying = false;
        }).catch(console.error);
      } else {
        this.userPaused = false;
        this.hasPlayed = true;
        this.player.play().then(() => {
          this.button.classList.add('is-playing');
          this.isPlaying = true;
        }).catch(console.error);
      }
    } else {
      if (this.isPlaying) {
        this.userPaused = true;
        this.player.pause();
        this.button.classList.remove('is-playing');
        this.isPlaying = false;
      } else {
        this.userPaused = false;
        this.hasPlayed = true;
        this.player.play().then(() => {
          this.button.classList.add('is-playing');
          this.isPlaying = true;
        }).catch(console.error);
      }
    }
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.videoContainer.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  showTimeline() {
    this.videoContainer.classList.remove('timeline-hover-out');
    this.videoContainer.classList.add('timeline-hover');
  }

  hideTimeline() {
    this.videoContainer.classList.remove('timeline-hover');
    this.videoContainer.classList.add('timeline-hover-out');
    setTimeout(() => {
      this.videoContainer.classList.remove('timeline-hover-out');
    }, 300);
  }

  startInactivityTimer() {
    this.clearInactivityTimer();
    this.inactivityTimeout = setInterval(() => {
      if (Date.now() - this.lastMouseMove >= 3000) {
        this.hideTimeline();
        this.clearInactivityTimer();
      }
    }, 100);
  }

  clearInactivityTimer() {
    if (this.inactivityTimeout) {
      clearInterval(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
  }
}

function initAllCustomVideoPlayers(root = document) {
  root.querySelectorAll('.video-player').forEach((videoContainer, index) => {
    const isMobile = window.innerWidth < 768;
    const deplay = isMobile ? 3000 : 0;

    setTimeout(() => {
      new CustomVideoPlayer(videoContainer);
    }, deplay * index);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllCustomVideoPlayers());
} else {
  initAllCustomVideoPlayers();
}

document.addEventListener('shopify:section:load', (event) => {
  initAllCustomVideoPlayers(event.target);
});
